import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";

import { mock } from "@/mock";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
// Estimator + pricing FAQ removed (simplified pricing page)

export default function Pricing() {
  const tiers = useMemo(() => mock.pricing.tiers, []);

  return (
    <div className="pb-20">
      <section className="mx-auto max-w-6xl px-6 pt-10 sm:px-10 sm:pt-16">
        <div className="flex flex-col gap-4">
          <Badge className="w-fit border-white/10 bg-white/5 text-white/75">
            Pricing
          </Badge>
          <h1 className="text-balance text-4xl font-semibold tracking-[-0.03em] text-white sm:text-6xl">
            Tiered transparency.
          </h1>
          <p className="max-w-2xl text-pretty text-[15px] leading-7 text-white/60">
            Start free. Upgrade when you need it. We are built to grow with you.
          </p>
        </div>

        <div className="mt-10 grid gap-4 lg:grid-cols-3">
          {tiers.map((t) => {
            const isPro = t.id === "pro";
            const isEnterprise = t.id === "enterprise";

            return (
              <Card
                key={t.id}
                className={
                  "relative overflow-hidden rounded-2xl border p-6 backdrop-blur-xl " +
                  (isPro
                    ? "border-emerald-400/30 bg-[linear-gradient(140deg,rgba(16,185,129,0.10),rgba(255,255,255,0.03)_55%,rgba(0,0,0,0.0))] shadow-[0_0_0_1px_rgba(16,185,129,0.10),0_30px_90px_rgba(0,0,0,0.55)]"
                    : isEnterprise
                      ? "border-violet-400/30 bg-white/[0.03] shadow-[0_0_0_1px_rgba(139,92,246,0.14),0_30px_90px_rgba(0,0,0,0.55)]"
                      : "border-white/10 bg-white/[0.03] shadow-[0_0_0_1px_rgba(255,255,255,0.03)]")
                }
              >
                {isPro && (
                  <div className="absolute right-4 top-4">
                    <Badge className="border-emerald-400/30 bg-emerald-400/10 text-emerald-100">
                      <Sparkles className="mr-1 h-3.5 w-3.5" /> Most popular
                    </Badge>
                  </div>
                )}

                {isEnterprise && (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -inset-px rounded-2xl bg-[radial-gradient(circle_at_30%_20%,rgba(139,92,246,0.20),transparent_40%),radial-gradient(circle_at_70%_70%,rgba(16,185,129,0.14),transparent_45%)]"
                  />
                )}

                <div className="relative">
                  <div className="text-[14px] font-medium text-white/85">
                    {t.name}
                  </div>
                  <div className="mt-2 flex items-end gap-2">
                    <div className="text-4xl font-semibold tracking-[-0.03em] text-white">
                      {t.price}
                    </div>
                    <div className="pb-1 text-[14px] text-white/50">
                      {t.period}
                    </div>
                  </div>

                  <div className="mt-2 text-[14px] text-white/60">
                    {t.tagline}
                  </div>

                  <div className="mt-5 grid gap-2">
                    {t.features.map((f) => (
                      <div
                        key={f}
                        className="flex items-start gap-2 text-[13px] leading-6 text-white/65"
                      >
                        <Check className="mt-0.5 h-4 w-4 text-emerald-300/80" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6">
                    <Button
                      asChild
                      className={
                        "h-11 w-full " +
                        (isEnterprise
                          ? "bg-white text-black hover:bg-white/90"
                          : isPro
                            ? ""
                            : "bg-white/[0.06] text-white hover:bg-white/[0.10]")
                      }
                      variant={isPro ? "default" : "secondary"}
                    >
                      <Link to={isEnterprise ? "/" : "/docs"}>{t.cta}</Link>
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </section>
    </div>
  );
}
